<script setup lang="ts">
import { isVNode } from 'vue'
import { Toast, ToastClose, ToastDescription, ToastProvider, ToastTitle, ToastViewport } from '.'
import { useToast } from './use-toast'

// The screen-reader texts reka-ui puts around every toast, translated by the app
// (this layer has no i18n dependency, as with DialogContent's `closeLabel`). Each
// falls back to reka-ui's English default when left out:
// - `label` is read before every toast's text ("Notification").
// - `viewportLabel` names the toast region landmark ("Notifications (F8)"). Pass a
//   function to receive the hotkey: a translated string with a literal `{hotkey}`
//   cannot be produced by vue-i18n, which would treat it as its own placeholder.
// - `closeLabel` is the accessible name of each toast's icon-only close button.
const props = defineProps<{
  label?: string
  viewportLabel?: string | ((hotkey: string) => string)
  closeLabel?: string
}>()

const { toasts } = useToast()
</script>

<template>
  <ToastProvider :label="props.label">
    <Toast v-for="toast in toasts" :key="toast.id" v-bind="toast">
      <div class="grid gap-1">
        <ToastTitle v-if="toast.title">
          {{ toast.title }}
        </ToastTitle>
        <template v-if="toast.description">
          <ToastDescription v-if="isVNode(toast.description)">
            <component :is="toast.description" />
          </ToastDescription>
          <ToastDescription v-else>
            {{ toast.description }}
          </ToastDescription>
        </template>
        <ToastClose :label="props.closeLabel" />
      </div>
      <component :is="toast.action" />
    </Toast>
    <ToastViewport :label="props.viewportLabel" />
  </ToastProvider>
</template>
